const SENSITIVE_PATH_PATTERNS = [
  {
    pattern: /(\/blog\/admin\/posts\/)[^/?#\s]+(\/edit(?:\/|$))/gi,
    replacement: '$1:postId$2'
  },
  {
    pattern: /(\/blog\/subscribe\/verify\/)[^/?#\s]+/gi,
    replacement: '$1:token'
  },
  {
    pattern: /(\/blog\/unsubscribe\/)[^/?#\s]+/gi,
    replacement: '$1:token'
  },
  {
    pattern: /(\/blog\/subscribe\/preferences\/)[^/?#\s]+/gi,
    replacement: '$1:token'
  },
  {
    pattern: /(\/blog\/subscribe\/status\/)[^/?#\s]+/gi,
    replacement: '$1:email'
  },
  {
    pattern: /(\/trading\/backtests\/shared\/)[^/?#\s]+/gi,
    replacement: '$1:token'
  },
  {
    pattern: /(\/(?:reset-password|verify-email)\/)[^/?#\s]+/gi,
    replacement: '$1:token'
  }
]

const SENSITIVE_KEYS = new Set([
  'accesstoken',
  'apikey',
  'authtoken',
  'authorization',
  'clientsecret',
  'code',
  'confirmpassword',
  'cookie',
  'email',
  'emailaddress',
  'password',
  'phone',
  'phonenumber',
  'pin',
  'refreshtoken',
  'secret',
  'sessiontoken',
  'setcookie',
  'temptoken',
  'token',
  'totp',
  'otp',
  'xxsrftoken'
])

const URL_FIELD_KEYS = new Set([
  'description',
  'filename',
  'from',
  'name',
  'previous',
  'referrer',
  'route',
  'to',
  'transaction',
  'url'
])

const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi
const SECRET_ASSIGNMENT_PATTERN =
  /(\b(?:[a-z0-9_-]*token|api[_-]?key|code|email|password|secret)=)[^&#\s]+/gi
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi

function normalizeKey(key) {
  return String(key || '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase()
}

function isSensitiveKey(key) {
  const normalized = normalizeKey(key)
  return (
    SENSITIVE_KEYS.has(normalized) ||
    normalized.endsWith('password') ||
    normalized.endsWith('secret') ||
    normalized.endsWith('token')
  )
}

function redactSensitivePaths(value) {
  return SENSITIVE_PATH_PATTERNS.reduce(
    (result, { pattern, replacement }) => result.replace(pattern, replacement),
    value
  )
}

export function sanitizeTelemetryPath(value) {
  if (typeof value !== 'string') return value

  const pathOnly = value.split(/[?#]/)[0]
  return redactSensitivePaths(pathOnly)
}

export function sanitizeTelemetryUrl(value) {
  if (typeof value !== 'string') return value

  const baseOrigin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://x3algo.invalid'

  try {
    const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(value)
    const isProtocolRelative = value.startsWith('//')
    const url = new URL(value, baseOrigin)
    const sanitizedPath = sanitizeTelemetryPath(url.pathname)

    if (isAbsolute || isProtocolRelative) {
      return `${url.origin}${sanitizedPath}`
    }

    return sanitizedPath
  } catch (_error) {
    return sanitizeTelemetryPath(value)
  }
}

export function isSensitiveTelemetryRoute(value) {
  if (typeof value !== 'string') return false

  let pathname
  let hasSensitiveQuery = false
  try {
    const url = new URL(value, 'https://x3algo.invalid')
    pathname = url.pathname
    hasSensitiveQuery = Array.from(url.searchParams.keys()).some(isSensitiveKey)
  } catch (_error) {
    pathname = value.split(/[?#]/)[0]
  }

  return (
    hasSensitiveQuery ||
    /^\/(?:reset-password|verify-email)(?:\/|$)/i.test(pathname) ||
    /^\/blog\/preferences(?:\/|$)/i.test(pathname) ||
    sanitizeTelemetryPath(pathname) !== pathname
  )
}

function looksLikeUrl(value) {
  return (
    value.startsWith('/') ||
    value.startsWith('//') ||
    /^[a-z][a-z\d+.-]*:\/\//i.test(value) ||
    /^[A-Z]+\s+(?:\/|[a-z][a-z\d+.-]*:\/\/)/i.test(value)
  )
}

function sanitizeUrlField(value) {
  const methodAndUrl = value.match(/^([A-Z]+\s+)(\S+)$/)
  if (methodAndUrl && looksLikeUrl(methodAndUrl[2])) {
    return `${methodAndUrl[1]}${sanitizeTelemetryUrl(methodAndUrl[2])}`
  }

  return looksLikeUrl(value)
    ? sanitizeTelemetryUrl(value)
    : redactSensitiveString(value)
}

function redactSensitiveString(value) {
  return redactSensitivePaths(value)
    .replace(SECRET_ASSIGNMENT_PATTERN, '$1[REDACTED]')
    .replace(BEARER_PATTERN, 'Bearer [REDACTED]')
    .replace(EMAIL_PATTERN, '[REDACTED_EMAIL]')
}

function scrubTelemetryValue(value, key = '') {
  if (isSensitiveKey(key)) return '[REDACTED]'

  if (typeof value === 'string') {
    return URL_FIELD_KEYS.has(normalizeKey(key)) || looksLikeUrl(value)
      ? sanitizeUrlField(value)
      : redactSensitiveString(value)
  }

  if (Array.isArray(value)) {
    return value.map(item => scrubTelemetryValue(item))
  }

  if (value && Object.prototype.toString.call(value) === '[object Object]') {
    return Object.fromEntries(
      Object.entries(value).map(([entryKey, entryValue]) => [
        entryKey,
        scrubTelemetryValue(entryValue, entryKey)
      ])
    )
  }

  return value
}

export function sanitizeTelemetryPayload(value) {
  return scrubTelemetryValue(value)
}
